import { FetchTarget_fetchTarget } from "_graphql/queries/__generated__/fetchTarget";
import { FC } from "react";
import moment from "moment"; // Import moment
import {
  CalendarIcon,
  CurrencyDollarIcon,
  GiftIcon,
  TagIcon,
} from "@heroicons/react/24/outline";

interface TargetViewProps {
  target: FetchTarget_fetchTarget | null;
}

// Helper function to format dates safely using moment
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "N/A";
  try {
    const date = moment(dateString);
    // Check if the date is valid after parsing
    if (!date.isValid()) {
      return dateString; // Fallback to the original string if parsing fails
    }
    // Format the date, e.g., "Jan 1st, 2024"
    return date.format("MMM Do, YYYY");
  } catch (error) {
    // Fallback in case of unexpected errors during formatting
    return dateString;
  }
};

// Helper function to format currency
const formatCurrency = (
  amount: number | null | undefined,
  currency: string | null | undefined
): string => {
  const resolvedCurrency = currency || "GHS"; // Default currency
  const value = amount ?? 0;
  try {
    return new Intl.NumberFormat(undefined, {
      // Use locale default formatting
      style: "currency",
      currency: resolvedCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch (error) {
    // Fallback for invalid currency codes or other errors
    return `${resolvedCurrency} ${value.toFixed(2)}`;
  }
};

const DetailItem: FC<{
  label: string;
  value: React.ReactNode;
  icon?: React.ElementType;
}> = ({ label, value, icon: Icon }) => (
  <div>
    <dt className="text-sm font-medium text-gray-500 flex items-center">
      {Icon && (
        <Icon className="h-4 w-4 mr-1.5 text-gray-400" aria-hidden="true" />
      )}
      {label}
    </dt>
    <dd className="mt-1 text-sm text-gray-900">{value || "N/A"}</dd>
  </div>
);

const TargetView: FC<TargetViewProps> = ({ target }) => {
  if (!target) {
    return (
      <div className="text-center py-10 text-gray-500">
        No target data available.
      </div>
    );
  }

  const baseCurrency = target.base_currency || "GHS";

  return (
    <div className="space-y-8 p-1">
      {" "}
      {/* Added slight padding */}
      {/* Target Information Section */}
      <section aria-labelledby="target-info-heading">
        <div className="bg-white shadow sm:rounded-lg overflow-hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3
              id="target-info-heading"
              className="text-lg leading-6 font-medium text-gray-900"
            >
              Target Information
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              General details about the target configuration.
            </p>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6 px-4 py-5 sm:px-6">
            <DetailItem label="Target Name" value={target.target_name} />
            <DetailItem
              label="Base Currency"
              value={baseCurrency}
              icon={CurrencyDollarIcon}
            />
            <DetailItem
              label="Start Date"
              value={formatDate(target.start_date)}
              icon={CalendarIcon}
            />
            <DetailItem
              label="End Date"
              value={formatDate(target.end_date)}
              icon={CalendarIcon}
            />
          </dl>
        </div>
      </section>
      {/* Badges Section */}
      <section aria-labelledby="badges-heading">
        <h3
          id="badges-heading"
          className="text-lg font-medium text-gray-900 mb-4"
        >
          Associated Badges
        </h3>
        {!target.target_badges?.length ? (
          <div className="text-center py-6 bg-gray-50 rounded-md border border-dashed border-gray-300">
            <TagIcon className="mx-auto h-10 w-10 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              No badges are associated with this target.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {target.target_badges.map(
              (badge) =>
                badge && (
                  <div
                    key={badge.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      {/* Badge Details */}
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                        <DetailItem
                          label="Badge Name"
                          value={badge.name}
                          icon={TagIcon}
                        />
                        <DetailItem
                          label="Description"
                          value={badge.description}
                        />
                        <DetailItem
                          label="Points Range"
                          value={`${badge.min_points ?? 0} - ${badge.max_points ?? "∞"}`}
                        />
                        <DetailItem
                          label="Cash Equivalent Range"
                          value={`${formatCurrency(badge.minimum_cash_equivalent, baseCurrency)} - ${formatCurrency(badge.cash_equivalent, baseCurrency)}`}
                          icon={CurrencyDollarIcon}
                        />
                      </dl>

                      {/* Gift Basket */}
                      {badge.gift_basket?.length ? (
                        <div className="mt-6 pt-4 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-600 flex items-center mb-2">
                            <GiftIcon
                              className="h-4 w-4 mr-1.5 text-gray-400"
                              aria-hidden="true"
                            />
                            Gift Basket Items
                          </h4>
                          <ul role="list" className="divide-y divide-gray-100">
                            {badge.gift_basket.map(
                              (gift, idx) =>
                                gift && (
                                  <li
                                    key={idx}
                                    className="py-2 flex justify-between items-start gap-x-4"
                                  >
                                    <div className="flex-auto">
                                      <p className="text-sm font-semibold leading-6 text-gray-900">
                                        {gift.gift_name || "Unnamed Gift"}
                                      </p>
                                      <p className="mt-1 text-xs leading-5 text-gray-500 truncate">
                                        {gift.gift_description ||
                                          "No description"}
                                      </p>
                                    </div>
                                    <div className="text-right flex-none">
                                      <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                                        {gift.gift_type || "Misc"}
                                      </span>
                                    </div>
                                  </li>
                                )
                            )}
                          </ul>
                        </div>
                      ) : (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-500 italic">
                            No gift basket items for this badge.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default TargetView;
